// ============================================================================
// Client-portal React Query hooks (self-service realm).
// ----------------------------------------------------------------------------
// All reads hang off the qk.client.* root and resolve "me" server-side from the
// client cookie — no id is ever passed. Self-log mutations invalidate the
// dashboard/metrics/list queries so the home screen and charts recompute right
// after the client saves.
// ============================================================================
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { clientApi, qk } from "../api";
import type {
  CalendarSubscription,
  ClientActivityInput,
  ClientPasswordChangePayload,
  MealInput,
  MeasurementInput,
  MessageInput,
  SessionInput,
  WaterInput,
} from "../api";
import type { ClientCalendarRange } from "../api/client";

/** Today's local date as 'YYYY-MM-DD' (the day filter the nutrition views use). */
export function todayDay(): string {
  const d = new Date();
  const tz = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - tz).toISOString().slice(0, 10);
}

export function useClientDashboard() {
  return useQuery({
    queryKey: qk.client.dashboard,
    queryFn: ({ signal }) => clientApi.dashboard(signal),
  });
}

// --- self-service: change own password (Wave 8) ----------------------------

/**
 * Change the signed-in client's own password (POST /api/client/me/password).
 * The existing session cookie stays valid (no re-login needed), so there is
 * nothing to invalidate — the caller surfaces success/error inline.
 */
export function useChangeClientPassword() {
  return useMutation({
    mutationFn: (body: ClientPasswordChangePayload) =>
      clientApi.changePassword(body),
  });
}

/**
 * The fancy dashboard aggregate (Wave 4) — ONE call feeding the animated rings,
 * the today hero, the weekly-goal checklist, next session + pending homework,
 * recent activity, and the mini weight trend. All the caller's OWN data. The
 * nutrition/activity/water invalidation helpers also refresh this key, so a log
 * re-fills the rings without a reload.
 */
export function useClientHome() {
  return useQuery({
    queryKey: qk.client.home,
    queryFn: ({ signal }) => clientApi.home(signal),
  });
}

export function useClientMetrics() {
  return useQuery({
    queryKey: qk.client.metrics,
    queryFn: ({ signal }) => clientApi.metrics(signal),
  });
}

export function useClientMeasurements() {
  return useQuery({
    queryKey: qk.client.measurements,
    queryFn: ({ signal }) => clientApi.measurements(signal),
  });
}

export function useClientSessions() {
  return useQuery({
    queryKey: qk.client.sessions,
    queryFn: ({ signal }) => clientApi.sessions(signal),
  });
}

export function useClientGoals(activeOnly = true) {
  return useQuery({
    queryKey: qk.client.goals(activeOnly),
    queryFn: ({ signal }) => clientApi.goals(activeOnly, signal),
  });
}

export function useClientReport(period = "all") {
  return useQuery({
    queryKey: qk.client.report(period),
    queryFn: ({ signal }) => clientApi.report(period, signal),
  });
}

/** Invalidate everything the home screen + charts derive from, after a write. */
function invalidatePortal(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: qk.client.dashboard });
  void qc.invalidateQueries({ queryKey: qk.client.home });
  void qc.invalidateQueries({ queryKey: qk.client.metrics });
  void qc.invalidateQueries({ queryKey: qk.client.measurements });
  void qc.invalidateQueries({ queryKey: qk.client.sessions });
  void qc.invalidateQueries({ queryKey: ["client", "report"] });
}

/** Invalidate the activity-derived views (list + merged calendar) after a log. */
function invalidateActivities(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: qk.client.activities });
  void qc.invalidateQueries({ queryKey: ["client", "calendar-entries"] });
  void qc.invalidateQueries({ queryKey: qk.client.dashboard });
  void qc.invalidateQueries({ queryKey: qk.client.home });
}

export function useAddClientMeasurement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: MeasurementInput) => clientApi.addMeasurement(body),
    onSuccess: () => invalidatePortal(qc),
  });
}

export function useAddClientSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SessionInput) => clientApi.addSession(body),
    onSuccess: () => invalidatePortal(qc),
  });
}

// --- activities (own self-logged + trainer-suggested homework) -------------

/** This client's own activities (self-logged + suggested homework). */
export function useClientActivities() {
  return useQuery({
    queryKey: qk.client.activities,
    queryFn: ({ signal }) => clientApi.activities(signal),
  });
}

/** The merged calendar union (own PT sessions + own/suggested activities). */
export function useClientCalendarEntries(range: ClientCalendarRange = {}) {
  return useQuery({
    queryKey: qk.client.calendarEntries(range.start, range.end),
    queryFn: ({ signal }) => clientApi.calendarEntries(range, signal),
  });
}

/** Self-log an exercise the client did (provenance forced to 'own', lime). */
export function useLogClientActivity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ClientActivityInput) => clientApi.logActivity(body),
    onSuccess: () => invalidateActivities(qc),
  });
}

/** Mark a trainer-suggested activity (homework, violet) done. */
export function useCompleteClientActivity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (activityId: number) => clientApi.completeActivity(activityId),
    onSuccess: () => invalidateActivities(qc),
  });
}

// --- calendar (ICS) subscription -------------------------------------------
// The client's own private, read-only feed of THEIR training only.

export function useClientCalendarSubscription() {
  return useQuery({
    queryKey: qk.client.calendar,
    queryFn: ({ signal }) => clientApi.calendarSubscription(signal),
    staleTime: 5 * 60 * 1000,
  });
}

export function useRotateClientCalendarSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => clientApi.rotateCalendarSubscription(),
    onSuccess: (next: CalendarSubscription) => {
      qc.setQueryData(qk.client.calendar, next);
    },
  });
}

// --- nutrition: meals + water + targets (Wave 2) ---------------------------

/** Invalidate the nutrition-derived views (meals/water/targets + dashboard). */
function invalidateNutrition(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ["client", "meals"] });
  void qc.invalidateQueries({ queryKey: qk.client.water });
  void qc.invalidateQueries({ queryKey: qk.client.nutritionTargets });
  void qc.invalidateQueries({ queryKey: qk.client.dashboard });
  void qc.invalidateQueries({ queryKey: qk.client.home });
  void qc.invalidateQueries({ queryKey: ["client", "calendar-entries"] });
}

/** This client's meals for a day (defaults to today) + the day's macro totals. */
export function useClientMeals(day = todayDay()) {
  return useQuery({
    queryKey: qk.client.meals(day),
    queryFn: ({ signal }) => clientApi.meals(day, signal),
  });
}

/** Self-log a meal (full macros; provenance forced to this client's trainer). */
export function useLogClientMeal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: MealInput) => clientApi.addMeal(body),
    onSuccess: () => invalidateNutrition(qc),
  });
}

/** Delete one of THIS client's meals. */
export function useDeleteClientMeal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (mealId: number) => clientApi.deleteMeal(mealId),
    onSuccess: () => invalidateNutrition(qc),
  });
}

/** Attach an encrypted photo (≤2 MB enforced server-side) to a meal. */
export function useUploadMealPhoto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ mealId, file }: { mealId: number; file: File }) =>
      clientApi.uploadMealPhoto(mealId, file),
    onSuccess: () => invalidateNutrition(qc),
  });
}

/** This client's daily water rows (oldest day first). */
export function useClientWater() {
  return useQuery({
    queryKey: qk.client.water,
    queryFn: ({ signal }) => clientApi.water(signal),
  });
}

/** Quick-add (or absolute-set) water for a day. */
export function useAddClientWater() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: WaterInput) => clientApi.addWater(body),
    onSuccess: () => invalidateNutrition(qc),
  });
}

/** Auto daily kcal/macro/water targets for THIS client (general guidance). */
export function useNutritionTargets() {
  return useQuery({
    queryKey: qk.client.nutritionTargets,
    queryFn: ({ signal }) => clientApi.nutritionTargets(signal),
  });
}

// --- weekly goal: THIS week's coach goal + progress checklist (Wave 3) ------

/**
 * THIS client's current-week weekly goal + progress checklist (read-only). The
 * coach sets it; the client only reads it. Live-syncs via the `weekly_goal`
 * broadcast (ENTITY_INVALIDATIONS → qk.client.weeklyGoal), so a goal set by the
 * coach surfaces on the dashboard without a reload.
 */
export function useClientWeeklyGoal() {
  return useQuery({
    queryKey: qk.client.weeklyGoal,
    queryFn: ({ signal }) => clientApi.weeklyGoal(signal),
  });
}

// --- messaging: THIS client's single thread with their trainer (Wave 5) -----
// `body` is plain UTF-8 user text + unicode emoji, rendered ESCAPED by the SPA.
// Live-syncs via the `message` broadcast (ENTITY_INVALIDATIONS → the client
// message + unread roots), so a message/read receipt from the trainer surfaces
// on the thread and the bottom-nav badge without a reload.

/** Invalidate the message thread + unread badge after a send/read. */
function invalidateMessages(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: qk.client.messagesRoot });
  void qc.invalidateQueries({ queryKey: qk.client.messagesUnread });
}

/** THIS client's thread (oldest→newest for display) + unread count. */
export function useClientMessages(beforeId?: number) {
  return useQuery({
    queryKey: qk.client.messages(beforeId),
    queryFn: ({ signal }) =>
      clientApi.messages.list({ before_id: beforeId }, signal),
  });
}

/**
 * The unread (trainer→client) badge count for the bottom-nav Messages tab.
 * Cheap, polled-free: kept fresh by the `message` live-sync invalidation. Refed
 * separately from the full thread so the badge updates even when the Messages
 * screen isn't mounted.
 */
export function useClientMessagesUnread() {
  return useQuery({
    queryKey: qk.client.messagesUnread,
    queryFn: ({ signal }) => clientApi.messages.unread(signal),
  });
}

/** Post a text message (provenance forced to 'client' server-side). */
export function useSendClientMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: MessageInput) => clientApi.messages.send(body),
    onSuccess: () => invalidateMessages(qc),
  });
}

/** Mark the thread read (stamps read_at on the TRAINER's unread messages). */
export function useMarkClientThreadRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => clientApi.messages.markRead(),
    onSuccess: () => invalidateMessages(qc),
  });
}

/** Send an image attachment (≤2 MB enforced server-side) + optional caption. */
export function useSendClientMessageAttachment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ file, body }: { file: File; body?: string }) =>
      clientApi.messages.sendAttachment(file, body),
    onSuccess: () => invalidateMessages(qc),
  });
}
